import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import type { AvailableSite, Me, Workspace } from '@jisy/shared';
import { apiRequest } from '../api/client.js';
import {
  availableSitesQuery,
  useWorkspaceConnect,
  useWorkspaceDisconnect,
  useWorkspaceSwitch,
  workspacesQuery,
} from '../api/queries.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import styles from './WorkspaceMenu.module.css';

/**
 * Переключатель пространств: адрес сайта в шапке открывает список подключённых сайтов Jira.
 *
 * Здесь же и подключение нового. Сайты, которые выданный токен уже открывает, подключаются
 * одним нажатием — за согласием в Atlassian идём только за теми, к которым доступа ещё нет.
 */
export function WorkspaceMenu({ me }: { me: Me }): React.ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  /** Пространство, которое просят отключить: подтверждение обязательно, действие видно не сразу. */
  const [leaving, setLeaving] = useState<Workspace | null>(null);

  const workspaces = useQuery(workspacesQuery);
  // Список доступных сайтов идёт в Atlassian, поэтому запрашивается только при открытом списке.
  const available = useQuery(availableSitesQuery(open));

  const switchTo = useWorkspaceSwitch();
  const connect = useWorkspaceConnect();
  const disconnect = useWorkspaceDisconnect();

  const busy = switchTo.isPending || connect.isPending || disconnect.isPending;
  const items = workspaces.data?.workspaces ?? [];
  const sites = available.data?.sites ?? [];

  /** Согласие в Atlassian: сюда уходят и переподключение, и сайт из другого аккаунта. */
  async function authorize(): Promise<void> {
    const { authUrl } = await apiRequest<{ authUrl: string }>('/api/auth/jira/start', {
      method: 'POST',
      body: {},
    });
    window.location.assign(authUrl);
  }

  function choose(workspace: Workspace): void {
    if (busy) return;
    if (workspace.needsReconnect) {
      void authorize();
      return;
    }
    if (workspace.current) {
      setOpen(false);
      return;
    }
    switchTo.mutate(workspace.tenantId);
  }

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button type="button" className={styles.trigger} aria-label={t('workspaces.title')}>
            <span className={`mono ${styles.triggerUrl}`}>{hostOf(me.tenant.siteUrl)}</span>
            <span className={styles.chevron} aria-hidden="true">
              ▾
            </span>
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content className={styles.content} align="start" sideOffset={6}>
            <div className={styles.heading}>{t('workspaces.title')}</div>

            <div className={styles.list}>
              {items.map((workspace) => (
                <div
                  key={workspace.tenantId}
                  className={`${styles.row} ${workspace.current ? styles.rowCurrent : ''}`}
                >
                  <button
                    type="button"
                    className={styles.pick}
                    onClick={() => choose(workspace)}
                    disabled={busy}
                  >
                    <SiteMark name={workspace.siteName} avatarUrl={workspace.avatarUrl} />
                    <span className={styles.text}>
                      <span className={`${styles.name} truncate`}>{workspace.siteName}</span>
                      <span className={`mono ${styles.host} truncate`}>
                        {workspace.needsReconnect
                          ? t('workspaces.needsReconnect')
                          : hostOf(workspace.siteUrl)}
                      </span>
                    </span>
                    {workspace.current && (
                      <span className={styles.check} aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </button>

                  {/*
                    Отключение стоит отдельной кнопкой, а не пунктом внутри строки: строка
                    переключает, и промахнуться между «войти» и «отключиться» нельзя.
                  */}
                  <button
                    type="button"
                    className={styles.leave}
                    onClick={() => setLeaving(workspace)}
                    disabled={busy}
                    title={t('workspaces.disconnect')}
                  >
                    <span aria-hidden="true">×</span>
                    <span className="visually-hidden">{t('workspaces.disconnect')}</span>
                  </button>
                </div>
              ))}
            </div>

            {sites.length > 0 && (
              <>
                <div className={styles.heading}>{t('workspaces.available')}</div>
                <div className={styles.list}>
                  {sites.map((site: AvailableSite) => (
                    <button
                      type="button"
                      key={site.cloudId}
                      className={`${styles.row} ${styles.pick}`}
                      onClick={() => connect.mutate(site.cloudId)}
                      disabled={busy}
                    >
                      <SiteMark name={site.name} avatarUrl={site.avatarUrl} />
                      <span className={styles.text}>
                        <span className={`${styles.name} truncate`}>{site.name}</span>
                        <span className={`mono ${styles.host} truncate`}>{hostOf(site.url)}</span>
                      </span>
                      <span className={styles.plus} aria-hidden="true">
                        +
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <button type="button" className={styles.addOther} onClick={() => void authorize()}>
              {t('workspaces.addOther')}
            </button>

            <p className={styles.hint}>{t('workspaces.hint')}</p>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {leaving && (
        <ConfirmDialog
          title={t('workspaces.disconnectTitle', { name: leaving.siteName })}
          body={t('workspaces.disconnectBody')}
          confirmLabel={t('workspaces.disconnect')}
          busy={disconnect.isPending}
          onConfirm={() => {
            disconnect.mutate(leaving.tenantId, { onSettled: () => setLeaving(null) });
          }}
          onCancel={() => setLeaving(null)}
        />
      )}
    </>
  );
}

/** Значок сайта. Своего значка у сайта может не быть — тогда первая буква названия. */
function SiteMark({
  name,
  avatarUrl,
}: {
  name: string;
  avatarUrl: string | null;
}): React.ReactElement {
  if (avatarUrl) {
    return <img className={styles.mark} src={avatarUrl} alt="" width={22} height={22} />;
  }

  return (
    <span className={`${styles.mark} ${styles.markLetter}`} aria-hidden="true">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
